import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import { get_run_url, intervention_response_url } from '@/utils/urls.ts';
import useAutomationId from '@/hooks/useAutomationId.ts';
import {
  AUTOMATION_RUN_KEY,
  AUTOMATION_RUNS_KEY,
  LATEST_STEP_RUNS_BASE_KEY,
  PROJECT_RUNS_KEY,
  RUN_LATEST_EXECUTION_KEY,
} from '@/utils/queryKeys.ts';
import { Run } from '@/types/runs.ts';
import { watchRun } from '@/features/runBrowserNotifications/watchedRuns.ts';

type SubmitInterventionResponseProps = {
  runId: string;
  stepRunId: string;
  expectedExecutionId: string;
  values: Record<string, string>;
};

const useSubmitInterventionResponse = () => {
  const apiClient = useApiClient();
  const automationId = useAutomationId();
  const queryClient = useQueryClient();

  return useMutation<unknown, AxiosError, SubmitInterventionResponseProps>({
    mutationFn: async ({ runId, stepRunId, expectedExecutionId, values }) => {
      const response = await apiClient.post(intervention_response_url(runId, stepRunId), {
        expected_execution_id: expectedExecutionId,
        values,
      });
      return response.data;
    },
    onSuccess: (_, { runId }) => {
      void apiClient
        .get<Run>(get_run_url(runId))
        .then((response) => watchRun({ automationId, run: response.data }))
        .catch(() => undefined);
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: AUTOMATION_RUNS_KEY(automationId) }),
        queryClient.invalidateQueries({ queryKey: PROJECT_RUNS_KEY }),
        queryClient.invalidateQueries({ queryKey: AUTOMATION_RUN_KEY(automationId, runId) }),
        queryClient.invalidateQueries({ queryKey: RUN_LATEST_EXECUTION_KEY(automationId, runId) }),
        queryClient.invalidateQueries({ queryKey: LATEST_STEP_RUNS_BASE_KEY(automationId, runId) }),
      ]);
    },
  });
};

export default useSubmitInterventionResponse;
